// react
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// shared components
import { AddButton, DeleteButton } from '@/components/ui/button';
import { RHFFieldLabel } from '@/components/ui/form';

// relative
import type { DataStreamOption } from '../types';
import type { AlarmConfigurationFormValues } from './alarmConfigurationForm.schema';

type UnitOption = {
  id: number;
  name: string;
  tags: string[];
};

type AlarmConfigurationDataStreamFieldsProps = {
  control: Control<AlarmConfigurationFormValues>;
  dataStreamOptions: DataStreamOption[];
};

type DataStreamAssociationRowProps = {
  index: number;
  value: string;
  unitOptions: UnitOption[];
  dataStreamOptions: DataStreamOption[];
  onChange: (value: string) => void;
  onRemove: () => void;
};

function DataStreamAssociationRow({
  index,
  value,
  unitOptions,
  dataStreamOptions,
  onChange,
  onRemove,
}: DataStreamAssociationRowProps) {
  const { t } = useTranslation('forms');

  const selectedOption = dataStreamOptions.find(
    (option) => option.key === value,
  );

  const selectedUnitId = selectedOption?.unitId ?? '';

  const selectedUnitOption =
    selectedUnitId === ''
      ? null
      : (unitOptions.find((option) => option.id === selectedUnitId) ?? null);

  const selectedUnitDataStreams =
    selectedUnitId === ''
      ? []
      : dataStreamOptions.filter((option) => option.unitId === selectedUnitId);

  return (
    <Stack spacing={2}>
      <Divider />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Typography variant="subtitle2">
          {t('alarmConfigurationForm.dataStreams.itemLabel')} {index + 1}
        </Typography>

        <DeleteButton aria-label={t('common.remove')} onClick={onRemove}>
          {t('common.delete')}
        </DeleteButton>
      </Stack>

      <Box>
        <Autocomplete
          fullWidth
          options={unitOptions}
          size="small"
          value={selectedUnitOption}
          isOptionEqualToValue={(option, selectedValue) =>
            option.id === selectedValue.id
          }
          getOptionLabel={(option) =>
            t('alarmConfigurationForm.dataStreams.unit.optionLabel', {
              id: option.id,
              name: option.name,
            })
          }
          filterOptions={(options, { inputValue }) => {
            const search = inputValue.trim().toLowerCase();

            if (!search) {
              return options;
            }

            return options.filter((option) => {
              const unitName = option.name.toLowerCase();
              const unitId = String(option.id);

              const tagMatch = option.tags.some((tag) =>
                tag.toLowerCase().includes(search),
              );

              return (
                unitName.includes(search) || unitId.includes(search) || tagMatch
              );
            });
          }}
          onChange={(_, nextUnitOption) => {
            const nextUnitId = nextUnitOption?.id;

            if (nextUnitId == null) {
              onChange('');
              return;
            }

            const firstDataStreamForUnit = dataStreamOptions.find(
              (option) => option.unitId === nextUnitId,
            );

            onChange(firstDataStreamForUnit?.key ?? '');
          }}
          renderInput={(params) => (
            <Box>
              <RHFFieldLabel htmlFor={params.id}>
                {t('alarmConfigurationForm.dataStreams.unit.label')}
              </RHFFieldLabel>

              <TextField
                {...params}
                placeholder={t(
                  'alarmConfigurationForm.dataStreams.unit.placeholder',
                )}
              />
            </Box>
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={t(
                    'alarmConfigurationForm.dataStreams.unit.optionLabel',
                    {
                      id: option.id,
                      name: option.name,
                    },
                  )}
                />

                {option.tags.map((tag) => (
                  <Chip
                    key={`${option.id}-${tag}`}
                    label={tag}
                    color="info"
                    size="small"
                  />
                ))}
              </Box>
            </li>
          )}
        />
      </Box>

      <Box>
        <RHFFieldLabel>
          {t('alarmConfigurationForm.dataStreams.dataType.label')}
        </RHFFieldLabel>

        <Select
          fullWidth
          size="small"
          value={value}
          disabled={selectedUnitDataStreams.length === 0}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          {selectedUnitDataStreams.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {t('alarmConfigurationForm.dataStreams.dataType.optionLabel', {
                dataType: option.dataType,
                unitTypeInputId: option.unitTypeInputId,
              })}
            </MenuItem>
          ))}
        </Select>

        {selectedUnitDataStreams.length === 0 ? (
          <FormHelperText>
            {t('alarmConfigurationForm.dataStreams.dataType.noneAvailable')}
          </FormHelperText>
        ) : null}
      </Box>
    </Stack>
  );
}

export function AlarmConfigurationDataStreamFields({
  control,
  dataStreamOptions,
}: AlarmConfigurationDataStreamFieldsProps) {
  const { t } = useTranslation('forms');

  const dedupedUnits = new Map<number, UnitOption>();

  for (const option of dataStreamOptions) {
    dedupedUnits.set(option.unitId, {
      id: option.unitId,
      name: option.unitName,
      tags: option.unitTags,
    });
  }

  const unitOptions = Array.from(dedupedUnits.values());

  const hasAvailableDataStreams = dataStreamOptions.length > 0;

  return (
    <Controller
      name="dataStreamKeys"
      control={control}
      render={({ field, fieldState }) => {
        const selectedDataStreamKeys = field.value;

        const updateDataStream = (index: number, value: string) => {
          const nextDataStreamKeys = [...selectedDataStreamKeys];

          nextDataStreamKeys[index] = value;

          field.onChange(nextDataStreamKeys);
        };

        const removeDataStream = (index: number) => {
          field.onChange(
            selectedDataStreamKeys.filter(
              (_, itemIndex) => itemIndex !== index,
            ),
          );
        };

        const addDataStream = () => {
          const firstUnusedOption =
            dataStreamOptions.find(
              (option) => !selectedDataStreamKeys.includes(option.key),
            ) ?? dataStreamOptions[0];

          if (!firstUnusedOption) {
            return;
          }

          field.onChange([...selectedDataStreamKeys, firstUnusedOption.key]);
        };

        return (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" fontSize="0.875rem">
                {t('alarmConfigurationForm.dataStreams.sectionLabel')} *
              </Typography>

              {selectedDataStreamKeys.length === 0 ? (
                <Typography variant="body2" color="error">
                  {t('alarmConfigurationForm.dataStreams.noneSelected')}
                </Typography>
              ) : null}
            </Box>

            {selectedDataStreamKeys.map((selectedDataStreamKey, index) => (
              <DataStreamAssociationRow
                key={index}
                index={index}
                value={selectedDataStreamKey}
                unitOptions={unitOptions}
                dataStreamOptions={dataStreamOptions}
                onChange={(value) => updateDataStream(index, value)}
                onRemove={() => removeDataStream(index)}
              />
            ))}

            <Box display="flex" justifyContent="flex-end">
              <AddButton
                onClick={addDataStream}
                disabled={!hasAvailableDataStreams}
              >
                {t('common.add')}
              </AddButton>
            </Box>

            {fieldState.error?.message ? (
              <FormHelperText error>{fieldState.error.message}</FormHelperText>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}
